import { getStoredUser, isMasterSession, isMasterOtpVerified } from "./impersonation";
import {
  normalizeStaffRoleKey,
  usesWaiterBaselinePermissions,
} from "./staffAccess";

const ELEVATED_ROLES = new Set(["owner", "admin", "super_admin", "superadmin", "master"]);
export const RBAC_OVERRIDES_KEY = "rbacOverridesV1";
const TEMP_DISABLED_FEATURES = new Set(["role_permissions"]);
const WAITER_SUPPORT_ROUTE_PREFIXES = [
  "/help",
  "/keyboard-shortcuts",
  "/login-help",
  "/policies",
  "/support",
  "/feedback",
];
const WAITER_SETTINGS_ROUTE_PREFIXES = ["/pos-settings"];
const WAITER_ONLY_ROUTE_PREFIXES = ["/kot/waiter", "/waiter/profile"];
const WAITER_QUICK_ORDER_PREFIX = "/order/takeaway";
export const ROLE_ORDER = [
  "owner",
  "admin",
  "super_admin",
  "manager",
  "cashier",
  "chef",
  "waiter",
  "attendant",
  "master",
];

export const normalizeRole = (user = getStoredUser()) => {
  const role = String(
    user?.role ||
      user?.user_role ||
      user?.staff_role ||
      user?.type ||
      user?.staff_type ||
      user?.user_type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (!role) return "unknown";
  if (role === "superadmin") return "super_admin";
  return normalizeStaffRoleKey(role);
};

export const DEFAULT_FEATURE_ACCESS = {
  dashboard: ["owner", "admin", "super_admin", "master", "manager", "cashier", "chef", "waiter", "attendant"],
  tables: ["owner", "admin", "super_admin", "master", "manager", "cashier", "waiter", "attendant"],
  orders: ["owner", "admin", "super_admin", "master", "manager", "cashier", "chef", "waiter", "attendant"],
  menu: ["owner", "admin", "super_admin", "master", "manager"],
  add_category: ["owner", "admin", "super_admin", "master", "manager", "cashier"],
  add_product: ["owner", "admin", "super_admin", "master", "manager", "cashier"],
  parties: ["owner", "admin", "super_admin", "master", "manager", "cashier"],
  inventory: ["owner", "admin", "super_admin", "master", "manager"],
  finance: ["owner", "admin", "super_admin", "master", "manager", "cashier"],
  reports: ["owner", "admin", "super_admin", "master", "manager", "cashier"],
  staff: ["owner", "admin", "super_admin", "master", "manager"],
  settings: ["owner", "admin", "super_admin", "master", "manager"],
  support: ["owner", "admin", "super_admin", "master", "manager", "cashier", "chef", "waiter", "attendant"],
  profile: ["owner", "admin", "super_admin", "master", "manager", "cashier", "chef", "waiter", "attendant"],
  role_permissions: [],
  impersonation: ["master"],
};
export const FEATURE_KEYS = Object.keys(DEFAULT_FEATURE_ACCESS);

const ROUTE_FEATURES = [
  { prefix: "/admin/impersonation", feature: "impersonation" },
  { prefix: "/login/master", feature: "impersonation" },
  { prefix: "/dashboard", feature: "dashboard" },
  { prefix: "/tables", feature: "tables" },
  { prefix: "/order/", feature: "orders" },
  { prefix: "/kot", feature: "orders" },
  { prefix: "/chef/dashboard", feature: "orders" },
  { prefix: "/chef/profile", feature: "profile" },
  { prefix: "/add-category", feature: "add_category" },
  { prefix: "/add-product", feature: "add_product" },
  { prefix: "/digital-menu", feature: "menu" },
  { prefix: "/add-party", feature: "parties" },
  { prefix: "/party-bills", feature: "parties" },
  { prefix: "/khatabook", feature: "finance" },
  { prefix: "/finance", feature: "finance" },
  { prefix: "/monthly-summary", feature: "finance" },
  { prefix: "/billing/history", feature: "finance" },
  { prefix: "/reports", feature: "reports" },
  { prefix: "/sales-report", feature: "reports" },
  { prefix: "/sales", feature: "reports" },
  { prefix: "/stock", feature: "inventory" },
  { prefix: "/inventory", feature: "inventory" },
  { prefix: "/product-reorder", feature: "inventory" },
  { prefix: "/staff/manage", feature: "staff" },
  { prefix: "/order-edit-history", feature: "reports" },
  { prefix: "/deleted-orders", feature: "reports" },
  { prefix: "/add-waiter", feature: "staff" },
  { prefix: "/role-permissions", feature: "role_permissions" },
  { prefix: "/profile", feature: "profile" },
  { prefix: "/linked-restaurants", feature: "profile" },
  { prefix: "/add-table", feature: "settings" },
  { prefix: "/pos-settings", feature: "settings" },
  { prefix: "/regional-tax-settings", feature: "settings" },
  { prefix: "/menu-customize", feature: "settings" },
  { prefix: "/multiple-printers", feature: "settings" },
  { prefix: "/printer-settings", feature: "settings" },
  { prefix: "/connect-bluetooth", feature: "settings" },
  { prefix: "/retry-print", feature: "settings" },
  { prefix: "/bulk-gst", feature: "settings" },
  { prefix: "/reset-account", feature: "settings" },
  { prefix: "/help", feature: "support" },
  { prefix: "/keyboard-shortcuts", feature: "support" },
  { prefix: "/login-help", feature: "support" },
  { prefix: "/policies", feature: "support" },
  { prefix: "/support", feature: "support" },
  { prefix: "/feedback", feature: "support" },
  { prefix: "/plan-selection", feature: "support" },
  { prefix: "/more", feature: "support" },
];

const sortedRouteFeatures = [...ROUTE_FEATURES].sort(
  (a, b) => b.prefix.length - a.prefix.length
);

const parseJson = (raw, fallback = null) => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const sanitizeRoles = (roles = []) =>
  Array.from(
    new Set(
      (Array.isArray(roles) ? roles : [])
        .map((role) => String(role || "").trim().toLowerCase())
        .filter((role) => ROLE_ORDER.includes(role))
    )
  );

const canUseStorage = () => typeof window !== "undefined" && Boolean(window?.localStorage);

const matchesPrefix = (path = "", prefixes = []) =>
  prefixes.some((prefix) => path.startsWith(prefix));

const normalizePath = (path = "") =>
  String(path || "")
    .trim()
    .toLowerCase();

export const normalizePackageType = (user = getStoredUser()) =>
  String(
    user?.package_type ||
      user?.packageType ||
      user?.package ||
      user?.plan_type ||
      user?.planType ||
      ""
  )
    .trim()
    .toLowerCase();

export const isStarterPackage = (user = getStoredUser()) =>
  normalizePackageType(user) === "starter";

export const isWaiterRole = (user = getStoredUser()) =>
  usesWaiterBaselinePermissions(normalizeRole(user));

export const getRbacOverrides = () => {
  if (!canUseStorage()) return {};
  const raw = parseJson(localStorage.getItem(RBAC_OVERRIDES_KEY), {});
  if (!raw || typeof raw !== "object") return {};
  return raw;
};

export const getFeatureAccessMatrix = () => {
  const overrides = getRbacOverrides();
  const merged = { ...DEFAULT_FEATURE_ACCESS };

  FEATURE_KEYS.forEach((feature) => {
    if (feature === "impersonation") return;
    if (!Object.prototype.hasOwnProperty.call(overrides, feature)) return;
    merged[feature] = sanitizeRoles(overrides[feature]);
  });

  return merged;
};

export const saveFeatureAccessMatrix = (matrix = {}) => {
  if (!canUseStorage()) return;
  const payload = {};

  FEATURE_KEYS.forEach((feature) => {
    if (feature === "impersonation") return;
    const next = sanitizeRoles(matrix?.[feature]);
    const current = sanitizeRoles(DEFAULT_FEATURE_ACCESS[feature]);
    if (JSON.stringify(next) !== JSON.stringify(current)) {
      payload[feature] = next;
    }
  });

  localStorage.setItem(RBAC_OVERRIDES_KEY, JSON.stringify(payload));
};

export const resetFeatureAccessMatrix = () => {
  if (!canUseStorage()) return;
  localStorage.removeItem(RBAC_OVERRIDES_KEY);
};

const hasFeatureForRole = (feature, role) => {
  const allowed = getFeatureAccessMatrix()[feature];
  if (!allowed) return true;
  return allowed.includes(role);
};

export const canAccessFeature = (feature, user = getStoredUser()) => {
  if (!feature) return true;
  if (TEMP_DISABLED_FEATURES.has(feature)) return false;
  if (!user) return false;

  if (feature === "impersonation") {
    return isMasterSession(user) || isMasterOtpVerified(user);
  }

  if (isMasterSession(user) || isMasterOtpVerified(user)) return true;

  const role = normalizeRole(user);
  if (ELEVATED_ROLES.has(role)) return true;
  return hasFeatureForRole(feature, role);
};

const routeToFeature = (path = "") => {
  const match = sortedRouteFeatures.find((item) => path.startsWith(item.prefix));
  return match?.feature || null;
};

const canWaiterAccessRoute = (path, user = getStoredUser()) => {
  const normalizedPath = normalizePath(path);
  if (!normalizedPath) return false;

  if (normalizedPath.startsWith("/dashboard")) {
    return false;
  }

  if (matchesPrefix(normalizedPath, WAITER_ONLY_ROUTE_PREFIXES)) {
    return normalizedPath.startsWith("/waiter/profile")
      ? canAccessFeature("profile", user)
      : canAccessFeature("orders", user);
  }

  if (matchesPrefix(normalizedPath, WAITER_SUPPORT_ROUTE_PREFIXES)) {
    return canAccessFeature("support", user);
  }

  if (matchesPrefix(normalizedPath, WAITER_SETTINGS_ROUTE_PREFIXES)) {
    return true;
  }

  if (normalizedPath.startsWith(WAITER_QUICK_ORDER_PREFIX)) {
    return canAccessFeature("orders", user);
  }

  if (normalizedPath.startsWith("/tables")) {
    return canAccessFeature("tables", user) && isStarterPackage(user);
  }

  if (normalizedPath.startsWith("/order/")) {
    return canAccessFeature("tables", user) && isStarterPackage(user);
  }

  return false;
};

export const getDefaultWebNavigation = (user = getStoredUser()) => {
  if (!user) return { path: "/login" };
  if (isMasterSession(user) || isMasterOtpVerified(user)) {
    return { path: "/login/master" };
  }
  if (isWaiterRole(user)) {
    if (isStarterPackage(user)) {
      return { path: "/tables" };
    }
    return { path: "/order/TakeAway-1", state: { quickBill: true } };
  }
  return { path: "/dashboard" };
};

export const getDefaultWebRoute = (user = getStoredUser()) =>
  getDefaultWebNavigation(user).path;

export const getProfileRoute = (user = getStoredUser()) =>
  isWaiterRole(user) ? "/waiter/profile" : "/profile";

export const canAccessRoute = (path, user = getStoredUser()) => {
  if (!user) return false;

  const normalizedPath = normalizePath(path);
  if (matchesPrefix(normalizedPath, WAITER_ONLY_ROUTE_PREFIXES)) {
    return isWaiterRole(user) && canWaiterAccessRoute(normalizedPath, user);
  }

  if (isWaiterRole(user)) {
    return canWaiterAccessRoute(normalizedPath, user);
  }

  return canAccessFeature(routeToFeature(path), user);
};

export const ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  super_admin: "Super Admin",
  manager: "Manager",
  cashier: "Cashier",
  chef: "Chef",
  waiter: "Waiter",
  attendant: "Attendant",
  master: "Master",
};

export const ROLE_COLORS = {
  owner: "#1d4ed8",
  admin: "#2563eb",
  super_admin: "#1e40af",
  manager: "#0f766e",
  cashier: "#b45309",
  chef: "#7c3aed",
  waiter: "#0f172a",
  attendant: "#334155",
  master: "#be123c",
};

export const buildRolePermissionSections = (matrix = getFeatureAccessMatrix()) =>
  ROLE_ORDER.map((role) => ({
    title: ROLE_LABELS[role] || role,
    role,
    color: ROLE_COLORS[role] || "#0f172a",
    features: FEATURE_KEYS.filter((feature) => (matrix[feature] || []).includes(role)),
  }));

export const FEATURE_LABELS = {
  dashboard: "Dashboard",
  tables: "Table & POS Sales",
  orders: "Orders & Quick Billing",
  menu: "Menu Management",
  add_category: "Add Category",
  add_product: "Add Product",
  parties: "Party / Customer",
  inventory: "Inventory",
  finance: "Finance & Khatabook",
  reports: "Reports Hub",
  staff: "Staff Management",
  settings: "POS / Printer Settings",
  support: "Help / Support",
  profile: "Profile",
  impersonation: "Admin Impersonation",
};

export const canEditPermissions = (user = getStoredUser()) => {
  if (!user) return false;
  if (isMasterSession(user) || isMasterOtpVerified(user)) return true;
  const role = normalizeRole(user);
  return ["owner", "admin", "super_admin"].includes(role);
};

export const canUseWebLogin = (user = getStoredUser()) => {
  if (!user) return false;
  if (isMasterSession(user) || isMasterOtpVerified(user)) return true;
  const role = normalizeRole(user);
  return (
    role === "owner" ||
    role === "cashier" ||
    usesWaiterBaselinePermissions(role)
  );
};
